import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { DateTimezonePipe } from '../../pipes/date-timezone.pipe';
import { DateRangePickerComponent } from '../date-range-picker';
import { SearchBarService } from '../search-bar/search-bar.service';
import { Choice } from '../select/choice.model';
import { TableFilterTypeEnum } from '../table/shared/enums';

@Component({
  selector: 'app-header-table-filter',
  templateUrl: './header-table-filter.component.html',
  styleUrls: ['./header-table-filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HeaderTableFilterComponent implements OnInit, OnDestroy {
  @Input() type: TableFilterTypeEnum;
  @Input() items: Choice[] = [];
  @Input() filterName: string;
  @Input() searchPlaceholderKey: string;

  keysSelectedSet = new Set();
  items$: Observable<Choice[]>;
  showChoices = false;
  searchCtrl = new FormControl('');
  dateRangeString: string;

  get choicesSelectedNumber(): number {
    return this.searchBarService.filtersMap?.get(this.filterName)?.size;
  }
  private subscriptions: Subscription = new Subscription();

  TableFilterTypeEnum = TableFilterTypeEnum;

  constructor(
    public searchBarService: SearchBarService,
    private dialog: MatDialog,
    private dateTimezonePipe: DateTimezonePipe
  ) {}

  ngOnInit(): void {
    this.items$ = this.searchCtrl.valueChanges.pipe(
      map(value => {
        return !value ? this.items : this.items.filter(({ label }) => new RegExp(value, 'gi').test(label));
      })
    );
    setTimeout(() => this.searchCtrl.reset());
    this.initFilter();
    this.subscriptions.add(
      this.searchBarService.clearFiltersBS.subscribe(() => {
        this.keysSelectedSet.clear();
        this.initFilter();
      })
    );
  }

  private initFilter() {
    if (this.type === TableFilterTypeEnum.DATE) {
      this.searchBarService.filtersMap.set(this.filterName, {
        start: null,
        end: null,
      });
    } else {
      this.searchBarService.filtersMap.set(this.filterName, new Set());
    }
  }

  toggleAllFilters() {
    if (this.keysSelectedSet.size !== this.items.length) {
      this.items.forEach(({ id }) => this.keysSelectedSet.add(id));
    } else {
      this.keysSelectedSet.clear();
    }
  }

  toggleFilter(id: any) {
    if (this.keysSelectedSet.has(id)) {
      this.keysSelectedSet.delete(id);
    } else {
      this.keysSelectedSet.add(id);
    }
  }

  updateTrigger(force?: boolean) {
    const dirty =
      this.keysSelectedSet.size ||
      this.keysSelectedSet.size !== this.searchBarService.filtersMap.get(this.filterName).size;
    this.searchBarService.filtersMap.set(this.filterName, new Set(this.keysSelectedSet));
    if (dirty || force) {
      this.searchBarService.searchAndSortBS.next(true);
    }
  }

  clear($event) {
    this.keysSelectedSet.clear();
    this.updateTrigger(true);
    $event.stopPropagation();
  }

  clearDateRange($event) {
    this.dateSelectionChanged({ start: null, end: null });
    $event.stopPropagation();
  }

  dateSelectionChanged(dateRange) {
    this.searchBarService.filtersMap.set(this.filterName, dateRange);
    this.searchBarService.searchAndSortBS.next(true);
  }

  openDateRangePicker($event: Event) {
    $event.stopPropagation();
    const dialogRef = this.dialog.open(DateRangePickerComponent, {
      hasBackdrop: true,
      data: { selectedDates: this.searchBarService.filtersMap.get(this.filterName) },
    });
    dialogRef.afterClosed().subscribe(dateRange => {
      if (dateRange) {
        this.dateSelectionChanged(dateRange);
        const { start, end } = dateRange;
        if (start) {
          this.dateRangeString = `${this.dateTimezonePipe.transform(
            moment(start).toDate()
          )} - ${this.dateTimezonePipe.transform(moment(end || start).toDate())}`;
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
